import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const featuredPrograms = [
  {
    icon: 'ri-rhythm-line',
    title: 'Drum Lessons & Development',
    description: 'Beginner-friendly instruction that builds strong rhythm, coordination, and confidence, progressing into performance and advanced skill.',
    color: 'from-[#B45309] to-brand-yellow-dark',
    eveningAvailable: true,
  },
  {
    icon: 'ri-music-fill',
    title: 'Bass Lessons & Development',
    description: 'Step-by-step instruction designed to build musical foundation, timing, and live performance ability.',
    color: 'from-brand-blue to-brand-blue-mid',
    eveningAvailable: true,
  },
  {
    icon: 'ri-music-2-line',
    title: 'Piano Lessons & Development',
    description: 'Structured lessons that guide students from the basics to advanced musical understanding and creative expression.',
    color: 'from-brand-purple-dark to-brand-purple',
    eveningAvailable: true,
  },
  {
    icon: 'ri-mic-2-line',
    title: 'Vocal Lessons & Development',
    description: 'Training focused on tone, control, and vocal strength, designed for all experience levels.',
    color: 'from-[#065F46] to-[#10B981]',
    eveningAvailable: true,
  },
  {
    icon: 'ri-user-voice-line',
    title: 'Vocal Coaching & Artistry',
    description: 'Personalized coaching focused on confidence, stage presence, and developing a unique vocal identity.',
    color: 'from-brand-purple to-brand-purple-light',
    eveningAvailable: true,
  },
  {
    icon: 'ri-headphone-line',
    title: 'Audio Production & Engineering',
    description: 'Hands-on training in recording, mixing, and mastering, equipping students with real-world skills in music production and studio workflow.',
    color: 'from-brand-blue-mid to-brand-purple-dark',
    eveningAvailable: true,
  },
];

const mediaPrograms = [
  {
    icon: 'ri-vidicon-2-line',
    title: 'Content Creation & Media Training',
    description: 'Learn how to shoot, edit, and produce high-quality video and social media content for modern platforms.',
    color: 'from-[#B45309] to-brand-yellow-dark',
    eveningAvailable: true,
  },
  {
    icon: 'ri-camera-2-line',
    title: 'Photography & Videography Training',
    description: 'Develop skills in camera operation, composition, lighting, and visual storytelling.',
    color: 'from-brand-blue to-brand-blue-mid',
    eveningAvailable: false,
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Songwriting & Creative Expression',
    description: 'Learn how to write, arrange, and creatively develop original music with structure and purpose.',
    color: 'from-[#065F46] to-[#10B981]',
    eveningAvailable: true,
  },
];

function PageHero() {
  return (
    <section className="relative min-h-[55vh] flex items-end overflow-hidden">
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#1E3A8A] via-[#1E2A6E] to-[#3730A3]" />
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/b3d2be80c0f6f1a2ee2c6923cf4628ba.jpg"
          alt="Creative Institute on-site programs"
          className="w-full h-full object-cover object-top opacity-30"
        />
      </div>
      <div className="absolute inset-0 pointer-events-none select-none z-[1]">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="absolute top-[-30px] right-[-10px] w-[560px] h-auto object-contain opacity-[0.15] mix-blend-screen"
        />
      </div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-yellow/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-8 pb-20 pt-36">
        <span className="hero-badge inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          Mobile-Based · We Come to You
        </span>
        <h1 className="hero-line-1 font-heading text-white text-6xl lg:text-7xl font-black mb-4">
          Creative Programs
        </h1>
        <p className="hero-sub font-body text-white/75 text-xl max-w-2xl leading-relaxed">
          Creative Institute offers structured, hands-on training across multiple creative disciplines — delivered directly to you. Each program is designed to build confidence, skill, and real-world ability in a professional and supportive environment, wherever you are.
        </p>
      </div>
    </section>
  );
}

function ProgramsGrid() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: gridRef, isVisible: gridVis } = useScrollAnimation({ threshold: 0.05 });
  const { ref: mediaRef, isVisible: mediaVis } = useScrollAnimation({ threshold: 0.2 });
  const { ref: mediaGridRef, isVisible: mediaGridVis } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section className="py-24 bg-gradient-to-b from-white via-[#F0F4FF] to-white">
      <div className="max-w-screen-xl mx-auto px-8">

        {/* Featured Programs */}
        <div ref={titleRef} className="text-center mb-14">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/20 text-brand-yellow-dark font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            Featured Programs
          </span>
          <h2 className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark`}>
            Music &amp; Vocal <span className="gradient-text">Development</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base mt-4 max-w-xl mx-auto`}>
            All programs are taught by experienced, passionate instructors in a structured and inclusive environment. Every creative mind is welcome.
          </p>
          <div className={`scroll-hidden delay-400 ${titleVis ? 'scroll-visible' : ''} inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-amber-50 border border-amber-200`}>
            <i className="ri-moon-line text-amber-600 text-sm" />
            <span className="font-body text-amber-700 text-xs font-medium">Programs marked <strong>Evening Available</strong> offer sessions until 9pm — Mon through Sat</span>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {featuredPrograms.map((prog, idx) => (
            <div
              key={prog.title}
              className={`scroll-hidden delay-${(idx % 3) * 100 + 100} ${gridVis ? 'scroll-visible' : ''} bg-white rounded-2xl border border-[#E0E7FF] overflow-hidden card-lift group flex flex-col`}
            >
              <div className={`h-2 bg-gradient-to-r ${prog.color}`} />
              <div className="p-7 flex flex-col flex-1">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${prog.color} text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${prog.icon} text-xl`} />
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-blue-dark mb-3">{prog.title}</h3>
                <p className="font-body text-gray-500 text-sm leading-relaxed flex-1">{prog.description}</p>
                {prog.eveningAvailable && (
                  <div className="mt-5 pt-4 border-t border-[#E0E7FF]">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-body text-xs font-semibold">
                      <i className="ri-moon-line text-xs" />
                      Evening Available · Until 9pm
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Creative Media Programs */}
        <div ref={mediaRef} className="text-center mb-14">
          <span className={`scroll-hidden ${mediaVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-4`}>
            Creative Media Programs
          </span>
          <h2 className={`scroll-hidden delay-200 ${mediaVis ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark`}>
            Media, Film &amp; <span className="gradient-text">Songwriting</span>
          </h2>
          <p className={`scroll-hidden delay-300 ${mediaVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base mt-4 max-w-xl mx-auto`}>
            Expand your creative toolkit beyond music into the world of visual storytelling, content, and original composition.
          </p>
        </div>

        <div ref={mediaGridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {mediaPrograms.map((prog, idx) => (
            <div
              key={prog.title}
              className={`scroll-hidden delay-${idx * 100 + 100} ${mediaGridVis ? 'scroll-visible' : ''} bg-white rounded-2xl border border-[#E0E7FF] overflow-hidden card-lift group flex flex-col`}
            >
              <div className={`h-2 bg-gradient-to-r ${prog.color}`} />
              <div className="p-7 flex flex-col flex-1">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${prog.color} text-white mb-5 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${prog.icon} text-xl`} />
                </div>
                <h3 className="font-heading text-xl font-bold text-brand-blue-dark mb-3">{prog.title}</h3>
                <p className="font-body text-gray-500 text-sm leading-relaxed flex-1">{prog.description}</p>
                {prog.eveningAvailable ? (
                  <div className="mt-5 pt-4 border-t border-[#E0E7FF]">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-body text-xs font-semibold">
                      <i className="ri-moon-line text-xs" />
                      Evening Available · Until 9pm
                    </span>
                  </div>
                ) : (
                  <div className="mt-5 pt-4 border-t border-[#E0E7FF]">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-50 border border-gray-200 text-gray-400 font-body text-xs">
                      <i className="ri-sun-line text-xs" />
                      Daytime Sessions Only
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

function SchedulingSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const slots = [
    { icon: 'ri-sun-line', label: 'Morning Sessions', desc: 'Daytime availability for students who prefer earlier hours and focused learning.', badge: null },
    { icon: 'ri-sun-foggy-line', label: 'Afternoon Sessions', desc: 'After-school and midday sessions designed to fit student and family schedules.', badge: null },
    { icon: 'ri-moon-line', label: 'Evening & Night Classes', desc: 'We proudly offer evening and late-night sessions for those with busy daytime schedules. Creativity does not have a time limit, and neither do we.', badge: { label: 'Night Classes', sub: 'Open Until 9pm' } },
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#2D1B69] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #F59E0B 0%, transparent 40%), radial-gradient(circle at 80% 50%, #A855F7 0%, transparent 40%)' }} />
      <div className="relative z-10 max-w-screen-xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-14`}>
          <span className="inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-5">
            Flexible Scheduling
          </span>
          <h2 className="font-heading text-5xl font-black text-white mb-4">
            Sessions That <span className="text-brand-yellow">Fit Your Life</span>
          </h2>
          <p className="font-body text-white/70 text-base max-w-2xl mx-auto leading-relaxed">
            We understand that every schedule is different. Creative Institute offers flexible session times throughout the day and into the evening so that no one misses out on the opportunity to grow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {slots.map((slot, i) => (
            <div
              key={slot.label}
              className={`scroll-hidden delay-${i * 100 + 100} ${isVisible ? 'scroll-visible' : ''} relative p-8 rounded-2xl bg-white/8 border card-lift group hover:bg-white/12 transition-colors ${slot.badge ? 'border-brand-yellow/50 hover:border-brand-yellow/80 ring-1 ring-brand-yellow/20' : 'border-white/15 hover:border-brand-yellow/40'}`}
            >
              {slot.badge && (
                <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                  <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-xs uppercase tracking-wide shadow-lg shadow-brand-yellow/30">
                    <i className="ri-moon-fill text-xs" />
                    {slot.badge.label}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/70 font-body text-[10px] tracking-wider">
                    {slot.badge.sub}
                  </span>
                </div>
              )}
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-brand-yellow/20 text-brand-yellow mb-6 group-hover:bg-brand-yellow group-hover:text-brand-blue-dark group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                <i className={`${slot.icon} text-2xl`} />
              </div>
              <h3 className="font-heading text-white text-xl font-bold mb-3">{slot.label}</h3>
              <p className="font-body text-white/65 text-sm leading-relaxed">{slot.desc}</p>
            </div>
          ))}
        </div>
        <div className={`scroll-hidden delay-400 ${isVisible ? 'scroll-visible' : ''} mt-4`}>
          <div className="rounded-2xl border border-brand-yellow/30 bg-white/5 px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/20 text-brand-yellow flex-shrink-0">
                <i className="ri-moon-fill text-xl" />
              </div>
              <div>
                <p className="font-heading text-white text-lg font-bold leading-tight">Ready to book an evening session?</p>
                <p className="font-body text-white/55 text-sm mt-0.5">
                  Open Mon–Sat &nbsp;·&nbsp; Until 9pm &nbsp;·&nbsp; Walk-ins Welcome &nbsp;·&nbsp; $30/hr
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link
                to="/enroll"
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm hover:bg-amber-300 transition-all cursor-pointer"
              >
                <i className="ri-moon-line text-sm" />
                Book a Night Session
              </Link>
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-white/25 text-white/70 font-body text-sm hover:border-white/50 hover:text-white transition-all cursor-pointer"
              >
                Have Questions?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyOnSite() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const perks = [
    { icon: 'ri-community-line', text: 'In-person mentorship and hands-on feedback' },
    { icon: 'ri-car-line', text: 'Mobile-based — we bring the session to you' },
    { icon: 'ri-shield-check-line', text: 'Safe, structured, and inclusive environment' },
    { icon: 'ri-heart-3-line', text: 'Neurodivergent-affirming approach and support' },
    { icon: 'ri-team-line', text: 'Community of fellow creatives at every level' },
    { icon: 'ri-moon-line', text: 'Evening and night sessions available for flexible scheduling' },
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF]">
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          <div className="flex-1">
            <span className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-purple/10 text-brand-purple font-body text-xs font-semibold uppercase tracking-widest mb-5`}>
              Why Come To Us
            </span>
            <h2 className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-heading text-5xl font-black text-brand-blue-dark mb-6`}>
              The Creative Institute<br /><span className="gradient-text">Difference</span>
            </h2>
            <p className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} font-body text-gray-600 text-base leading-relaxed mb-8`}>
              When you work with Creative Institute, you step into an experience built specifically for creatives. Every detail — from the curriculum to the personalized approach — is designed to unlock potential and build excellence, right where you are.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {perks.map((perk, i) => (
                <div key={i} className={`scroll-hidden delay-${i * 100 + 300} ${isVisible ? 'scroll-visible' : ''} flex items-start gap-3`}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple flex-shrink-0">
                    <i className={`${perk.icon} text-sm`} />
                  </div>
                  <p className="font-body text-gray-600 text-sm leading-snug pt-1">{perk.text}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`scroll-hidden-right ${isVisible ? 'scroll-visible' : ''} flex-shrink-0 w-full lg:w-[460px] h-[400px] rounded-3xl overflow-hidden`}>
            <img
              src="https://readdy.ai/api/search-image?query=students%20in%20music%20arts%20workshop%20learning%20together%2C%20bright%20cheerful%20studio%20classroom%2C%20royal%20blue%20and%20gold%20color%20accents%2C%20inclusive%20warm%20environment%2C%20professional%20instructor%20guiding%20group%2C%20modern%20facility%20with%20instruments%2C%20high%20quality%20photography&width=460&height=400&seq=onsite-perks002&orientation=landscape"
              alt="Students learning at Creative Institute"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function EnrollCTA() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.3 });
  return (
    <section ref={ref} className="py-20 bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white text-center">
      <div className="max-w-screen-md mx-auto px-8">
        <h2 className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} font-heading text-5xl font-black mb-4`}>
          Ready to Begin?
        </h2>
        <p className={`scroll-hidden delay-200 ${isVisible ? 'scroll-visible' : ''} font-body text-white/75 text-lg mb-8`}>
          Enrollment is open. Secure your spot in one of our on-site programs today.
        </p>
        <div className={`scroll-hidden delay-300 ${isVisible ? 'scroll-visible' : ''} flex flex-wrap justify-center gap-4`}>
          <Link to="/enroll" className="whitespace-nowrap px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-base hover:bg-brand-yellow-light transition-all cursor-pointer">
            Enroll Now
          </Link>
          <Link to="/contact" className="whitespace-nowrap px-8 py-4 rounded-full border-2 border-white/50 text-white font-body font-semibold text-base hover:border-brand-yellow hover:text-brand-yellow transition-all cursor-pointer">
            Ask a Question
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function OnSiteProgramsPage() {
  return (
    <main>
      <PageHero />
      <ProgramsGrid />
      <SchedulingSection />
      <WhyOnSite />
      <EnrollCTA />
    </main>
  );
}
